import {
  Autocomplete,
  Box,
  Button,
  Grid,
  ListItemText,
  TextField,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  IResearchQueryDetail,
  IResearchQuery,
} from "../../types/researchQuery";
import useFetchResourceColumnsById from "../../hooks/useFetchResourceColumnsById";
import useFetchResourceColumnsByMultipleIds from "../../hooks/useFetchResourceColumnsByMultipleIds";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import {
  GenericResourceFormSchema,
  GenericResourceQuerySchema,
  resourceQuerySchema,
} from "../../schemas";
import { extractVariables } from "../../services/utils";
import * as Yup from "yup";
import GenericQueryBuilderDialog from "./GenericQueryBuilderDialog";
import ViewResourceIconButton from "../../components/ViewResourceIconButton";

interface GenericResourceQueryTabProps {
  handleTransition: any;
  researchQueryData: IResearchQueryDetail;
  setResearchQueryData: any;
  resourcesData: any;
  renderFrom: string;
  columns: any;
  setColumns: Dispatch<SetStateAction<any>>;
  isEditQuery: any;
  setIsEditQuery: any;
  editFormData: any;
  setQueryType: any;
  setEditFormData: any;
  handleSaveResearchQuery?: (researchQuery: IResearchQuery[]) => void;
  setEditorValue?: any;
  setIsAddQueryBtnEnabled?: any;
  isFromExecution?: boolean;
  setErrors?: any;
}

const GenericResourceQueryTab = ({
  handleSaveResearchQuery,
  researchQueryData,
  setResearchQueryData,
  handleTransition,
  resourcesData,
  renderFrom,
  columns,
  setColumns,
  isEditQuery,
  setIsEditQuery,
  editFormData,
  setQueryType,
  setEditFormData,
  setEditorValue,
  setIsAddQueryBtnEnabled,
  isFromExecution = false,
  setErrors,
}: GenericResourceQueryTabProps) => {
  const {
    setIsLoading,
    editorRef,
    queryBuilderTempValue,
    setQueryBuilderTempValue,
    globalVariables,
    tempGlobalVariables,
    setTempGlobalVariables,
    setGlobalVariables,
    hasChanges,
    setHasChanges,
  } = useRuleResourceContext();
  const [formData, setFormData] = useState<any>({
    query: "SELECT * FROM <>",
  });
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [resourceColumnId, setResourceColumnId] = useState<any>(0);
  const [queryError, setQueryError] = useState<{ [key: string]: string }>({});
  const [mergedQueryColumns, setMergedQueryColumns] = useState<any>();
  const [resColumns, setResColumns] = useState<any>([]);
  const [variables, setVariables] = useState<any>([]);
  const [openQueryDialogIndex, setOpenQueryDialogIndex] = useState<
    number | null
  >(null);
  const [additionalResourceColIds, setAdditionalResourceColIds] = useState<
    number[]
  >([]);

  const [fetchedresourceColumnData] = useFetchResourceColumnsById({
    resourceColumnId,
    setIsLoading,
  });

  // Fetch additional resource columns
  const [additionalResourceColumnsData] = useFetchResourceColumnsByMultipleIds({
    resourceColIds: additionalResourceColIds,
    setIsLoading,
  });
  useEffect(() => {
    if (renderFrom === "resource") {
      setFormData((prev: any) => ({
        ...prev,
        source: {
          ...prev?.source,
          resource_id: resourcesData?.id,
        },
      }));
    }
  }, []);

  useEffect(() => {
    setResColumns([]);
    if (fetchedresourceColumnData) {
      const columns =
        fetchedresourceColumnData?.resource_column_properties?.resource_columns?.map(
          (col: any) => ({
            name: col?.column_name,
            value: col?.column_name,
          }),
        ) || [];

      setResColumns(columns);
    }
  }, [fetchedresourceColumnData]);

  useEffect(() => {
    if (resColumns && formData?.source) {
      setColumns((prev: any) => ({
        ...prev,
        resourceColumns: [
          {
            name: formData?.source?.resource_code ?? "",
            columns: resColumns,
          },
        ],
        mergedColumns: [],
      }));
    }
  }, [resColumns]);

  // Detect and fetch additional resource columns for the selected resource
  useEffect(() => {
    try {
      // Check if a resource is selected
      if (!formData?.source?.resource_id) {
        setAdditionalResourceColIds([]);
        return;
      }

      // Find the selected resource
      const selectedResource = resourcesData?.find(
        (r: any) => r.id === formData.source.resource_id,
      );

      if (!selectedResource) {
        setAdditionalResourceColIds([]);
        return;
      }

      // Check if resource has additional_resource_data
      const additionalResourceData =
        selectedResource?.additional_properties?.additional_resource_data;

      if (
        !additionalResourceData ||
        !Array.isArray(additionalResourceData) ||
        additionalResourceData.length === 0
      ) {
        // No additional resources, clear the state
        setAdditionalResourceColIds([]);
        return;
      }

      // Extract additional resource IDs
      const additionalResourceIds = additionalResourceData
        .map((addRes: any) => addRes.resource_id)
        .filter(Boolean);

      if (additionalResourceIds.length === 0) {
        setAdditionalResourceColIds([]);
        return;
      }

      // Find resource_column_details_id for each additional resource
      const additionalResourceColumnIds = additionalResourceIds
        .map((resId: number) => {
          const resource = resourcesData?.find((r: any) => r.id === resId);
          return resource?.additional_properties?.resource_column_details_id;
        })
        .filter(Boolean);

      // Set the additional resource column IDs to trigger fetching
      setAdditionalResourceColIds(additionalResourceColumnIds);
    } catch (error) {
      console.error("Error detecting additional resource columns:", error);
      setAdditionalResourceColIds([]);
    }
  }, [formData?.source?.resource_id, resourcesData]);

  // Process fetched additional resource columns with conflict resolution
  useEffect(() => {
    try {
      if (
        !additionalResourceColumnsData ||
        additionalResourceColumnsData.length === 0
      ) {
        // No additional resource columns, clear the state
        setColumns((prev: any) => ({
          ...prev,
          additionalResourceColumns: [],
        }));
        return;
      }

      // Get base resource columns
      const baseResourceColumns = resColumns || [];

      // Count column name occurrences across all resources
      const columnNameCounts = new Map<string, number>();

      // Count base resource columns
      baseResourceColumns.forEach((col: any) => {
        const colName = col.name || col.value;
        columnNameCounts.set(colName, (columnNameCounts.get(colName) || 0) + 1);
      });

      // Count additional resource columns
      additionalResourceColumnsData.forEach((resourceData: any) => {
        const columns =
          resourceData?.resource_column_properties?.resource_columns || [];
        columns.forEach((col: any) => {
          const colName = col.column_name;
          columnNameCounts.set(
            colName,
            (columnNameCounts.get(colName) || 0) + 1,
          );
        });
      });

      // Get the selected resource's additional_resource_data
      const selectedResource = resourcesData?.find(
        (r: any) => r.id === formData?.source?.resource_id,
      );
      const additionalResourceData =
        selectedResource?.additional_properties?.additional_resource_data || [];

      // Process each additional resource
      const processedAdditionalResources = additionalResourceColumnsData.map(
        (resourceData: any, index: number) => {
          const columns =
            resourceData?.resource_column_properties?.resource_columns || [];

          // Find the corresponding additional resource data to get resource_id
          const additionalResData = additionalResourceData[index];
          const resourceId = additionalResData?.resource_id;

          // Find the actual resource object to get resource_name and resource_code
          const actualResource = resourcesData?.find(
            (r: any) => r.id === resourceId,
          );
          const resourceName =
            actualResource?.resource_name || `Resource ${index + 1}`;
          const resourceCode = actualResource?.code || `RES_${index + 1}`;

          // Process columns with conflict resolution
          const processedColumns = columns.map((col: any) => {
            const columnName = col.column_name;
            const count = columnNameCounts.get(columnName) || 0;

            // Apply suffix only if there's a conflict (count > 1)
            const nameWithSuffix =
              count > 1 ? `${columnName}_${resourceCode}` : columnName;

            return {
              name: nameWithSuffix, // Name with suffix (used for insertion)
              originalName: columnName, // Original name (used for display)
              value: nameWithSuffix,
            };
          });

          return {
            name: resourceName,
            resourceCode: resourceCode,
            columns: processedColumns,
          };
        },
      );

      // Update columns state with additional resource columns
      setColumns((prev: any) => ({
        ...prev,
        additionalResourceColumns: processedAdditionalResources,
      }));
    } catch (error) {
      console.error("Error processing additional resource columns:", error);
      setColumns((prev: any) => ({
        ...prev,
        additionalResourceColumns: [],
      }));
    }
  }, [
    additionalResourceColumnsData,
    resColumns,
    formData?.source?.resource_id,
    resourcesData,
  ]);

  useEffect(() => {
    let resColumnId: string | number = 0;
    let resourceCode: string = "";
    if (renderFrom === "rule") {
      resColumnId = resourcesData?.find(
        (option: { id: any }) => option.id === formData?.source?.resource_id,
      )?.additional_properties?.resource_column_details_id;
      resourceCode = resourcesData?.find(
        (option: { id: any }) => option.id === formData?.source?.resource_id,
      )?.code;
    } else if (renderFrom === "resource") {
      resColumnId =
        resourcesData?.additional_properties?.resource_column_details_id;
    }
    if (!isEditQuery) {
      setQueryBuilderTempValue(
        `SELECT * FROM ${resourceCode ? `<${resourceCode}>` : "<>"}`,
      );
    }
    setResourceColumnId(resColumnId);
  }, [formData?.source?.resource_id, renderFrom]);
  useEffect(() => {
    const queryVariables: any = extractVariables(queryBuilderTempValue);
    setVariables(queryVariables ?? []);
  }, [queryBuilderTempValue]);

  useEffect(() => {
    if (variables?.length > 0 && globalVariables) {
      const globalVars: any = {};
      variables.forEach((variable: any) => {
        if (globalVariables[variable] !== undefined) {
          globalVars[variable] = globalVariables[variable];
        } else if (tempGlobalVariables[variable] !== undefined) {
          globalVars[variable] = tempGlobalVariables[variable];
        } else {
          globalVars[variable] = "";
        }
      });
      // Compare the current tempLocalVariables with the new ones to avoid unnecessary updates
      if (JSON.stringify(tempGlobalVariables) !== JSON.stringify(globalVars)) {
        // setTempLocalVariables(globalVars);
        setTempGlobalVariables(globalVars);
      }
    } else {
      setTempGlobalVariables({});
    }
  }, [variables]);

  const validateField = async (name: any, value: any) => {
    try {
      // Create a partial form data object with only the field being changed
      const partialFormData = { ...formData, [name]: value };
      await GenericResourceQuerySchema.validateAt(name, partialFormData);
      // If validation passes, clear any previous errors for this field
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const validateSourceField = async (name: any, value: any) => {
    try {
      const partialFormData = {
        source: {
          [name]: value,
        },
      };
      const validateFieldName = `source.${name}`;
      await resourceQuerySchema.validateAt(validateFieldName, partialFormData);
      // If validation passes, clear any previous errors for this field
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };
  const handleChangeQuery = (event: any) => {
    const { name, value } = event.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value);
  };
  const handleSaveQuery = async () => {
    try {
      const researchQuery: any = [...researchQueryData?.research_queries];
      // Validate GenericResourceFormSchema first
      // Check for duplicate query names
      const isDuplicate = researchQuery.some(
        (query: any) =>
          !(isEditQuery && query.id === formData.id) &&
          query.name?.toLowerCase().trim() ===
            formData.name?.toLowerCase().trim(),
      );
      setQueryError((prev: any) => ({
        ...prev,
        name: isDuplicate ? "Query name is duplicate" : "",
      }));
      if (isDuplicate) return;

      if (!isFromExecution) {
        try {
          await GenericResourceFormSchema.validate(researchQueryData, {
            abortEarly: false,
          });
        } catch (formErrors: any) {
          const newErrors: { [key: string]: string } = {};
          if (formErrors.inner) {
            formErrors.inner.forEach(
              (error: { path: string | number; message: string }) => {
                // const fieldName = String(error.path).replace(/^source?\./, "");
                newErrors[error.path] = error.message;
              },
            );
          }
          setErrors((prevErrors: any) => ({
            ...prevErrors,
            ...newErrors,
          }));
          return;
        }
      }

      const updatedFormData = {
        ...formData,
        query: queryBuilderTempValue,
      };

      // Then validate resourceQuerySchema
      await resourceQuerySchema.validate(updatedFormData, {
        abortEarly: false,
      });

      // Variable validations
      const variableSchema: any = {};
      Object.keys(tempGlobalVariables).map((key: any) => {
        if (tempGlobalVariables[key] === "") {
          variableSchema[key] = Yup.string().required(`Please enter ${key}`);
          setQueryError((prevErrors) => ({
            ...prevErrors,
            [key]: `Please enter ${key}`,
          }));
        } else {
          return {
            name: key,
            value: tempGlobalVariables[key],
          };
        }
      });
      const resourceQueryVariableSchema = Yup.object().shape(variableSchema);
      await resourceQueryVariableSchema.validate(tempGlobalVariables, {
        abortEarly: false,
      });
      // setGlobalVariables((prev: any) => ({
      //   ...Object.keys(tempGlobalVariables).reduce(
      //     (acc, key) => ({ ...acc, [key]: tempGlobalVariables[key] }),
      //     prev
      //   ),
      // }));

      if (isEditQuery) {
        const index = researchQuery.findIndex(
          (item: any) => item.id === formData.id,
        );
        if (index !== -1) {
          researchQuery[index] = {
            id: formData.id,
            name: formData.name,
            query: queryBuilderTempValue,
            //updatedQuery: updatedQuery,
            source: [
              {
                type: "Resource",
                resource_id: formData?.source?.resource_id,
                resource_code: formData?.source?.resource_code,
              },
            ],
          };
        }
      } else {
        researchQuery.push({
          id: researchQuery.length > 0 ? researchQuery.length + 1 : 1,
          name: formData?.name,
          query: queryBuilderTempValue,
          //updatedQuery: updatedQuery,
          source: [
            {
              type: "Resource",
              resource_id: formData?.source?.resource_id,
              resource_code: formData?.source?.resource_code,
            },
          ],
        });
      }

      setResearchQueryData((prev: any) => ({
        ...prev,
        research_queries: researchQuery,
      }));

      setFormData({ name: "" });
      setTempGlobalVariables({});
      setQueryBuilderTempValue(`SELECT * FROM <>`);
      handleTransition(false);
      setQueryType("");
      setIsEditQuery(false);
      setIsAddQueryBtnEnabled(true);

      handleSaveResearchQuery && handleSaveResearchQuery(researchQuery);
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      if (validationErrors.inner) {
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            const fieldName = String(error.path).replace(/^source?\./, "");
            newErrors[fieldName] = error.message;
          },
        );
      }
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  const handleColumnSelect = (column: string, columnName: string) => {
    if (!editorRef.current) return;
    const editor = editorRef.current.editor;
    if (!editor) return;

    const currentPosition = editor.getCursorPosition();
    const currentColumn = currentPosition.column;

    let updatedColumn = `${column} `;

    if (columnName === "missing") {
      updatedColumn = `"${column}" `;
    } else if (columnName === "mismatched") {
      updatedColumn = `"${column}" `;
    } else if (columnName === "merged") {
      updatedColumn = `"${column}" `;
    }

    editor.session.insert(currentPosition, updatedColumn);
    setEditorValue(editor.session.getValue());

    const newCursorPosition = {
      row: currentPosition.row,
      column: currentColumn + updatedColumn.length,
    };

    editor.moveCursorToPosition(newCursorPosition);
    editor.clearSelection();
    editor.focus();
  };

  useEffect(() => {
    if (isEditQuery) {
      const resColumnId = resourcesData?.find((option: { id: any }) => {
        return option.id === editFormData?.source?.resource_id;
      })?.additional_properties?.resource_column_details_id;
      setResourceColumnId(resColumnId);
      setFormData(editFormData);
      setInitialFormData(JSON.parse(JSON.stringify(editFormData)));
    }
  }, [editFormData, resourcesData]);

  useEffect(() => {
    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
      queryBuilderTempValue !== formData?.query;
    setHasChanges(formChanged);
  }, [formData, initialFormData, queryBuilderTempValue]);

  const handleResourceChange = (event: any, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      source: {
        ...prev.source,
        ["resource_id"]: value?.id,
        ["resource_code"]: value?.code,
      },
    }));
    setQueryBuilderTempValue(
      `SELECT * FROM <${value?.code ? value?.code : ""}>`,
    );
    setResColumns([]);
    validateSourceField("resource_id", value?.id);

    const resource_var = value?.additional_properties?.inline_variables;
    if (resource_var) {
      setGlobalVariables(resource_var);
    }
  };

  return (
    <>
      <Grid item xl={3} lg={3} md={3} sm>
        <TextField
          label={
            <span>
              Query Name
              <span className="required-asterisk">*</span>
            </span>
          }
          name="name"
          type="text"
          fullWidth
          variant="outlined"
          value={formData?.name}
          onChange={handleChangeQuery}
          error={!!queryError?.name}
          helperText={queryError?.name || ""}
          className={`form-control-autocomplete ${
            queryError?.name ? "has-error" : ""
          }`}
        />
      </Grid>
      {renderFrom === "rule" && (
        <>
          <Grid item xl={3} lg={3} md={3} sm>
            <Box className="d-flex align-items-end cols-gap-6">
              <Autocomplete
                fullWidth
                options={resourcesData ?? []}
                getOptionLabel={(option: any) => option.resource_name || ""}
                value={
                  resourcesData?.find(
                    (option: { id: any }) =>
                      option.id === formData?.source?.resource_id,
                  ) || null
                }
                renderInput={(params) => (
                  <TextField
                    label={
                      <span>
                        Select Resource
                        <span className="required-asterisk">*</span>
                      </span>
                    }
                    name="resource"
                    style={{ color: "#000000" }}
                    {...params}
                    placeholder="Select..."
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!queryError?.resource_id}
                    helperText={queryError?.resource_id || ""}
                  />
                )}
                renderOption={(params: any, item: any) => (
                  <li
                    {...params}
                    key={item.key}
                    style={{
                      paddingTop: "2px",
                      paddingBottom: "2px",
                    }}
                  >
                    <ListItemText>{item.resource_name}</ListItemText>
                  </li>
                )}
                loadingText="Loading..."
                onChange={(event: any, value: any) =>
                  handleResourceChange(event, value)
                }
                // className={`form-control-autocomplete`}
                className={`form-control-autocomplete
                            ${queryError?.resource_id ? "has-error" : ""}
                          `}
              />
              {formData?.source?.resource_id && (
                <ViewResourceIconButton
                  resourceId={formData?.source?.resource_id}
                  tooltipText={"Click to view resource"}
                />
              )}
            </Box>
          </Grid>
        </>
      )}
      {renderFrom === "resource" && (
        <Grid item xl={3} lg={3} md={3} sm>
          <TextField
            label={
              <span>
                Resource Name
                <span className="required-asterisk">*</span>
              </span>
            }
            type="text"
            fullWidth
            variant="outlined"
            className="form-control-autocomplete"
            value={resourcesData?.resource_name}
            disabled={true}
          />
        </Grid>
      )}

      <GenericQueryBuilderDialog
        columns={columns}
        setColumns={setColumns}
        index={15}
        openQueryDialogIndex={openQueryDialogIndex}
        setOpenQueryDialogIndex={setOpenQueryDialogIndex}
        isTextBoxRequied={true}
        queryType={"resource"}
        queryBuilderType={"resource_query"}
      />
      <Grid
        item
        xs
        sx={{
          display: "flex",
          columnGap: "8px",
          textAlign: "right",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          className="btn-orange btn-dark"
          onClick={() => {
            setFormData({
              name: "",
            });
            setQueryBuilderTempValue(`SELECT * FROM <>`);
            setQueryType("");
            setQueryError({});
            setEditFormData({
              name: "",
            });
            handleTransition(false);
            setIsEditQuery(false);
            setIsAddQueryBtnEnabled(true);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          className="btn-orange"
          onClick={() => handleSaveQuery()}
          disabled={!hasChanges}
        >
          <SaveOutlinedIcon /> Save
        </Button>
      </Grid>
    </>
  );
};

export default GenericResourceQueryTab;
